import AccountPicker from "../../components/AccountPicker";
import { useEffect, useState, type Dispatch, type SetStateAction } from "react";
import { Ban, ChevronRight, Pencil, Trash2 } from "lucide-react";
import type { Account, DashboardData, IOU, Transaction } from "../../types";
import { dateLabel, money, shortName } from "../../lib/finance";
import type { ConfirmState } from "../../components/ConfirmDialog";
import LoadingLabel from "../../components/LoadingLabel";
import FormActions from "../../components/FormActions";
import Modal from "../../components/Modal";
import PayeeAvatar from "../../components/PayeeAvatar";
import { iouNeed, type Need } from "../../lib/coverage";
import { useLedgerNeed } from "../../app/use-ledger-need";
import RepaidBar from "./RepaidBar";
import type { LoanEditDraft, PaymentEditDraft, SettleDraft } from "./iou-drafts";

/** One loan: how much is left, the transactions behind it, and what can be done to it. */
export default function IouDetail({
  detail, data, ensure, accounts, payeeName, error, setError, busy, settling, setSettling,
  setEditingIou, setEditingPayment, setConfirm, onOpenTransaction, onClose,
  onSaveSettlement, onWriteOff, onDelete,
}: {
  detail: IOU;
  data: DashboardData;
  /** Loads the ledger back far enough to list the loan's entries. */
  ensure: (need: Need) => unknown;
  accounts: Account[];
  payeeName: (id: string) => string;
  error: string;
  setError: (message: string) => void;
  busy: boolean;
  settling: SettleDraft | null;
  setSettling: Dispatch<SetStateAction<SettleDraft | null>>;
  setEditingIou: (draft: LoanEditDraft | null) => void;
  setEditingPayment: (draft: PaymentEditDraft | null) => void;
  setConfirm: (confirm: ConfirmState | null) => void;
  onOpenTransaction: (t: Transaction) => void;
  onClose: () => void;
  onSaveSettlement: () => void;
  onWriteOff: (reason?: string) => void;
  onDelete: () => void;
}) {
  const entriesReady = useLedgerNeed(data, iouNeed(data, detail.id), ensure);
  const [writingOff, setWritingOff] = useState(false);
  const [writeOffReason, setWriteOffReason] = useState("");

  useEffect(() => {
    setWritingOff(false);
    setWriteOffReason("");
  }, [detail.id]);

  const writeOffMatch = detail.status === "Written Off" ? detail.description.match(/\(Write-off:\s*(.*?)\)$/) : null;
  const cleanDescription = writeOffMatch ? detail.description.replace(/\s*\(Write-off:.*?\)$/, "") : detail.description;
  const writeOffReasonText = writeOffMatch ? writeOffMatch[1] : "";

  // The ledger itself, not the Settlements mirror: this also surfaces the funding leg, which the
  // settlement rows never cover, and shows the real thing if the two ever drift apart. The loan
  // leads regardless of date, since a same-day repayment would otherwise sort above it.
  const fundingType = detail.direction === "Owed to us" ? "expense" : "income";
  const detailTransactions = (data.transactions || [])
    .filter((t) => t.iouId === detail.id)
    .sort((a, b) => {
      const funding = (t: Transaction) => (t.type.toLowerCase() === fundingType ? 0 : 1);
      return funding(a) - funding(b) || a.date.localeCompare(b.date);
    });
  // Money out of an "Owed to us" loan is the lending; money in is being repaid. A "We owe" loan
  // is the mirror image.
  const legLabel = (t: Transaction) => {
    const out = t.type.toLowerCase() === "expense";
    if (detail.direction === "Owed to us") return out ? "Lent" : "Repaid to you";
    return out ? "Paid back" : "Borrowed";
  };
  // A repayment leg's transaction is what the Settlements row points at, so this is the link back
  // from a row in the list to the record that can be edited.
  const settlementFor = (t: Transaction) => detail.settlements.find((x) => x.transactionId === t.id);
  return (
    <Modal
      title="IOU details"
      onClose={onClose}
      headerActions={
        !settling && !writingOff && (
          <>
            <button
              className="icon-button"
              onClick={() => {
                setEditingIou({
                  description: cleanDescription || detail.description,
                  originalAmount: String(detail.originalAmount),
                  dueDate: detail.dueDate ? detail.dueDate.slice(0, 10) : "",
                  fundingAccountId: detail.fundingAccountId,
                });
                setError("");
              }}
              aria-label="Edit IOU"
            >
              <Pencil size={18} />
            </button>
            <button
              className="icon-button danger-icon-button"
              onClick={() =>
                setConfirm({
                  title: "Delete this IOU?",
                  message: `The loan, its ${detail.settlements.length} recorded payment${detail.settlements.length === 1 ? "" : "s"} and every transaction behind them will be removed. This can't be undone.`,
                  confirmLabel: "Delete",
                  danger: true,
                  onConfirm: () => onDelete(),
                })
              }
              aria-label="Delete IOU"
            >
              <Trash2 size={18} />
            </button>
          </>
        )
      }
    >
      <div className="detail-hero">
        <PayeeAvatar id={detail.payeeId} name={payeeName(detail.payeeId)} size={56} />
        <h3>{cleanDescription || detail.description}</h3>
        <strong>{money(detail.outstanding, detail.currency)}</strong>
        <p>Outstanding of {money(detail.originalAmount, detail.currency)}</p>
      </div>
      <RepaidBar original={detail.originalAmount} outstanding={detail.outstanding} />
      <dl className="detail-list">
        {[
          ["Payee", payeeName(detail.payeeId)],
          ["Direction", detail.direction],
          ["Status", detail.status],
          ["Write-off reason", writeOffReasonText],
          ["Created", detail.createdDate ? dateLabel(detail.createdDate) : ""],
          ["Due", detail.dueDate ? dateLabel(detail.dueDate) : ""],
          ["Created by", detail.createdBy],
        ]
          .filter(([, v]) => v)
          .map(([k, v]) => (
            <div key={k}>
              <dt>{k}</dt>
              <dd>{v}</dd>
            </div>
          ))}
      </dl>
      {!entriesReady && <p className="muted empty-small" role="status"><LoadingLabel>Loading</LoadingLabel></p>}
      {entriesReady && detailTransactions.length > 0 && (
        <div className="linked-txn-list">
          {detailTransactions.map((t) => {
            const income = t.type.toLowerCase() === "income";
            return (
              <div key={t.id} className="linked-txn-row">
                <button type="button" className="linked-txn-main" onClick={() => onOpenTransaction(t)}>
                  <span>
                    <strong>{legLabel(t)}</strong>
                    <small>{dateLabel(t.date)} · {shortName((data.accounts || []).find((a) => a.id === t.accountId)?.name || t.accountId)}</small>
                  </span>
                  <span className={income ? "income" : ""}>
                    {income ? "+" : "−"}{money(t.amount, t.currency)}
                    <ChevronRight size={15} />
                  </span>
                </button>
                {settlementFor(t) && (
                  <button type="button" className="linked-txn-edit" aria-label="Edit this repayment" onClick={() => { const s = settlementFor(t)!; setEditingPayment({ id: s.id, amount: String(s.amount), date: s.date.slice(0, 10), accountId: s.accountId, notes: s.notes }); setError(""); }}>
                    <Pencil size={14} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
      {error && <div className="inline-error" role="alert">{error}</div>}
      {detail.status === "Open" && (
        settling?.iouId === detail.id ? (
          <form className="account-editor" onSubmit={(e) => { e.preventDefault(); onSaveSettlement(); }}>
            <label>Amount<input required inputMode="decimal" autoFocus value={settling.amount} onChange={(e) => setSettling({ ...settling, amount: e.target.value })} /></label>
            <AccountPicker label="Account" accounts={accounts.filter(a => a.currency === detail.currency)} value={settling.accountId} onChange={(id) => setSettling({ ...settling, accountId: id })} required />
            <label><span className="field-label">Notes <small>Optional</small></span><input value={settling.notes} onChange={(e) => setSettling({ ...settling, notes: e.target.value })} /></label>
            <FormActions
              label="Record payment"
              busy={busy}
              disabled={!settling.accountId}
              secondary={<button type="button" className="form-actions-muted" onClick={() => setSettling(null)}>Back to loan</button>}
            />
          </form>
        ) : writingOff ? (
          <form className="lost-confirm-box" onSubmit={(e) => { e.preventDefault(); onWriteOff(writeOffReason.trim()); }}>
            <div className="lost-confirm-head">
              <Ban size={16} />
              <strong>Write off this IOU?</strong>
            </div>
            <p>
              The remaining {money(detail.outstanding, detail.currency)} will be closed without a payment. This can’t be undone.
            </p>
            <label className="write-off-reason-field">
              <span className="field-label">Reason / note <small>(optional)</small></span>
              <input
                type="text"
                placeholder="e.g. Forgiven, uncollectible, dispute settled"
                value={writeOffReason}
                onChange={(e) => setWriteOffReason(e.target.value)}
                disabled={busy}
                autoFocus
              />
            </label>
            <FormActions
              label="Write off"
              busy={busy}
              busyLabel="Writing off"
              secondary={
                <button
                  type="button"
                  className="form-actions-muted"
                  disabled={busy}
                  onClick={() => {
                    setWritingOff(false);
                    setWriteOffReason("");
                  }}
                >
                  Back to loan
                </button>
              }
            />
          </form>
        ) : (
          <div className="iou-card-actions">
            <button className="secondary-action" onClick={() => { setSettling({ iouId: detail.id, amount: String(detail.outstanding), accountId: "", notes: "" }); setError(""); }}>Record a payment</button>
            <button className="text-button subtle" onClick={() => { setWritingOff(true); setError(""); }}><Ban size={14} /> Write off</button>
          </div>
        )
      )}
    </Modal>
  );
}
